/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


/**
 * @author Yann Massard (yamass@gmail.com)
 */
import * as $ from "jquery";
import * as log from "loglevel";
import {UiTextCellTemplateConfig} from "../generated/UiTextCellTemplateConfig";
import {UiItemViewConfig} from "../generated/UiItemViewConfig";
import {UiItemView} from "./UiItemView";
import {AbstractUiToolContainer} from "./AbstractUiToolContainer";
import {TeamAppsEvent} from "./util/TeamAppsEvent";
import {UiToolbarButtonGroupConfig} from "../generated/UiToolbarButtonGroupConfig";
import {UiToolbarButtonConfig} from "../generated/UiToolbarButtonConfig";
import {UiToolbarDropDownButtonConfig} from "../generated/UiToolbarDropDownButtonConfig";
import {createUiDropDownButtonClickInfoConfig, UiDropDownButtonClickInfoConfig} from "../generated/UiDropDownButtonClickInfoConfig";
import {DEFAULT_TEMPLATES} from "trivial-components";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {enterFullScreen, exitFullScreen, isFullScreen} from "./Common";
import {UiToolAccordionCommandHandler, UiToolAccordionConfig, UiToolAccordionEventSource} from "../generated/UiToolAccordionConfig";
import {AbstractUiToolContainer_ToolbarButtonClickEvent, AbstractUiToolContainer_ToolbarDropDownItemClickEvent} from "../generated/AbstractUiToolContainerConfig";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";
import {EventFactory} from "../generated/EventFactory";

enum DropDownInitializationStatus {
	UNINITIALIZED, LOADING, INITIALIZED
}

interface Button {
	config: UiToolbarButtonConfig;
	visible: boolean;
	$buttonWrapper: JQuery;
	$button: JQuery;
	optimizedWidth?: number;
	$dropDown?: JQuery;
	$dropDownSourceButtonIndicator?: JQuery;
	dropDownItemViewConfig?: UiItemViewConfig;
	dropDownItemView?: UiItemView;
	dropDownInitializationStatus?: DropDownInitializationStatus;
}

export class UiToolAccordion extends AbstractUiToolContainer<UiToolAccordionConfig> implements UiToolAccordionCommandHandler, UiToolAccordionEventSource {

	public static DEFAULT_TOOLBAR_MAX_HEIGHT = 70;

	public readonly onToolbarButtonClick: TeamAppsEvent<AbstractUiToolContainer_ToolbarButtonClickEvent> = new TeamAppsEvent<AbstractUiToolContainer_ToolbarButtonClickEvent>(this);
	public readonly onToolbarDropDownItemClick: TeamAppsEvent<AbstractUiToolContainer_ToolbarDropDownItemClickEvent> = new TeamAppsEvent<AbstractUiToolContainer_ToolbarDropDownItemClickEvent>(this);

	private buttonGroupsById: { [index: string]: UiButtonGroup } = {};

	private $mainDomElement: JQuery;
	private $backgroundColorDiv: JQuery;

	constructor(config: UiToolAccordionConfig, context: TeamAppsUiContext) {
		super(config, context);

		this.$mainDomElement = $(`<div class="UiToolAccordion teamapps-blurredBackgroundImage"></div>`);
		this.$backgroundColorDiv = $('<div class="background-color-div"></div>')
			.css("background-color", config.backgroundColor)
			.appendTo(this.$mainDomElement);

		for (let i = 0; i < config.buttonGroups.length; i++) {
			const buttonGroupConfig = config.buttonGroups[i];
			let buttonGroup = this.createButtonGroup(buttonGroupConfig);
			this.buttonGroupsById[buttonGroupConfig.groupId] = buttonGroup;
			buttonGroup.getMainDomElement().appendTo(this.$backgroundColorDiv)
		}

		this.refreshEnforcedButtonWidth();
	}

	public getMainDomElement(): JQuery {
		return this.$mainDomElement;
	}

	private createButtonGroup(buttonGroupConfig: UiToolbarButtonGroupConfig): UiButtonGroup {
		return new UiButtonGroup(buttonGroupConfig, this, this._context, AbstractUiToolContainer.$sizeTestingContainer);
	}

	protected onAttachedToDom() {
		for (let buttonGroupId in this.buttonGroupsById) {
			let buttonGroup = this.buttonGroupsById[buttonGroupId];
			buttonGroup.onAttachedToDom();
		}
		this.reLayout();
	}

	public setDropDownData(buttonId: string, itemView: UiItemViewConfig): void {
		this.getAllButtonGroups().forEach(buttonGroup => buttonGroup.setDropDownData(buttonId, itemView));
	}

	private getAllButtonGroups(): UiButtonGroup[] {
		return Object.keys(this.buttonGroupsById)
			.map(key => this.buttonGroupsById[key]);
	}

	public setButtonVisible(buttonId: string, visible: boolean) {
		this.getAllButtonGroups().forEach(buttonGroup => buttonGroup.setButtonVisible(buttonId, visible));
		this.refreshEnforcedButtonWidth();
	}

	public setButtonGroupVisible(groupId: string, visible: boolean) {
		this.getAllButtonGroups().forEach(buttonGroup => buttonGroup.setVisible(visible));
		this.refreshEnforcedButtonWidth();
	}

	public addButtonGroup(buttonGroupConfig: UiToolbarButtonGroupConfig) {
		const existingButtonGroup = this.buttonGroupsById[buttonGroupConfig.groupId];
		if (existingButtonGroup) {
			this.removeButtonGroup(buttonGroupConfig.groupId);
		}

		const buttonGroup = this.createButtonGroup(buttonGroupConfig);
		this.buttonGroupsById[buttonGroupConfig.groupId] = buttonGroup;

		let allButtonGroups = this.getAllButtonGroups().sort((group1, group2) => group1.position - group2.position);
		let otherGroupIndex = 0;
		for (; otherGroupIndex < allButtonGroups.length; otherGroupIndex++) {
			let otherGroup = allButtonGroups[otherGroupIndex];
			if (otherGroup.position > buttonGroupConfig.position) {
				break;
			}
		}

		if (allButtonGroups[otherGroupIndex]) {
			buttonGroup.getMainDomElement().insertBefore(allButtonGroups[otherGroupIndex].getMainDomElement());
		} else {
			buttonGroup.getMainDomElement().appendTo(this.$backgroundColorDiv);
		}

		this.refreshEnforcedButtonWidth();
	}

	public removeButtonGroup(groupId: string): void {
		const buttonGroup = this.buttonGroupsById[groupId];

		if (buttonGroup) {
			delete this.buttonGroupsById[groupId];
			buttonGroup.getMainDomElement().remove();
		}
		this.refreshEnforcedButtonWidth();
	}

	public addButton(groupId: string, buttonConfig: UiToolbarButtonConfig, neighborButtonId: string, beforeNeighbor: boolean) {
		this.buttonGroupsById[groupId] && this.buttonGroupsById[groupId].addButton(buttonConfig, neighborButtonId, beforeNeighbor);
		this.refreshEnforcedButtonWidth();
	}

	public removeButton(buttonId: string): void {
		this.getAllButtonGroups().forEach(group => group.removeButton(buttonId));
		this.refreshEnforcedButtonWidth();
	}

	updateButtonGroups(buttonGroups: UiToolbarButtonGroupConfig[]): void {
		// TODO implement only if really needed
	}

	public refreshEnforcedButtonWidth() {
		let maxButtonWidth = Object.keys(this.buttonGroupsById)
			.map(groupId => this.buttonGroupsById[groupId])
			.filter(group => group.isVisible())
			.reduce((maxButtonWidth, buttonGroup) => Math.max(maxButtonWidth, buttonGroup.getMaxOptimizedButtonWidth()), 1);
		Object.keys(this.buttonGroupsById).forEach((groupId) => {
			const group = this.buttonGroupsById[groupId];
			group.setEnforcedButtonWidth(maxButtonWidth);
		});
	}

	public onResize(): void {
		Object.keys(this.buttonGroupsById).forEach((groupId) => {
			this.buttonGroupsById[groupId].onResize();
		});
	}

	public destroy(): void {
		// TODO ?
	}
}

class UiButtonGroup {
	private static logger = log.getLogger("UiToolAccordion.UiButtonGroup");

	private attachedToDom: boolean = false;
	private config: UiToolbarButtonGroupConfig;
	private visible: boolean = true;
	private $buttonGroupWrapper: JQuery;
	private $buttonGroup: JQuery;
	private buttonsById: { [index: string]: Button } = {};
	private buttons: Button[] = [];
	private $buttonRows: JQuery[] = [];
	private enforcedButtonWidth: number = 1;

	constructor(buttonGroupConfig: UiToolbarButtonGroupConfig, private toolAccordion: UiToolAccordion, private context: TeamAppsUiContext, private $sizeTestingContainer: JQuery) {
		const $buttonGroupWrapper = $('<div class="button-group-wrapper"/>');

		const $buttonGroup = $(`<div class="toolbar-button-group" id="${this.toolAccordionId}_${buttonGroupConfig.groupId}">`)
			.appendTo($buttonGroupWrapper);

		this.config = buttonGroupConfig;
		this.$buttonGroupWrapper = $buttonGroupWrapper;
		this.$buttonGroup = $buttonGroup;

		for (let j = 0; j < buttonGroupConfig.buttons.length; j++) {
			this.addButton(buttonGroupConfig.buttons[j]);
		}

		this.setVisible(buttonGroupConfig.visible);
	}

	private get toolAccordionId() {
		return this.toolAccordion.getId();
	}

	public get position() {
		return this.config.position;
	}

	private createButton(buttonConfig: UiToolbarButtonConfig): Button {
		const $buttonWrapper = $('<div class="toolbar-button-wrapper" data-buttonId="' + buttonConfig.buttonId + '" tabindex="0">');
        let renderer = this.context.templateRegistry.createTemplateRenderer(buttonConfig.template);
		const $button = $(renderer.render(buttonConfig.recordData)).appendTo($buttonWrapper);

		if (buttonConfig._type === 'UiToolbarDropDownButton') {
			$buttonWrapper.append(
				'<div class="toolbar-button-caret">' +
				'  <div class="caret"></div>' +
				'</div>'
			);
		}

		const button: Button = {
			config: buttonConfig,
			$buttonWrapper: $buttonWrapper,
			$button: $button,
			visible: buttonConfig.visible
		};

		AbstractUiToolContainer.optimizeButtonWidth(button, (buttonConfig.template as UiTextCellTemplateConfig).maxHeight || UiToolAccordion.DEFAULT_TOOLBAR_MAX_HEIGHT);

		$buttonWrapper.toggleClass("hidden", !buttonConfig.visible);

		if (buttonConfig._type === 'UiToolbarDropDownButton') {
			this.createDropDown(button, $buttonWrapper);
		}

		return button;
	}

	private createDropDown(button: Button, $buttonWrapper: JQuery) {
		const dropDownButtonConfig = <UiToolbarDropDownButtonConfig> button.config;
		button.$dropDown = $(`<div class="tool-accordion-dropdown"></div>`);
		button.$dropDownSourceButtonIndicator = $(`<div class="source-button-indicator">`).appendTo(button.$dropDown);

		button.dropDownItemViewConfig = dropDownButtonConfig.staticView;
		if (!dropDownButtonConfig.staticView) {
			$(DEFAULT_TEMPLATES.defaultSpinnerTemplate).appendTo(button.$dropDown);
			button.dropDownInitializationStatus = DropDownInitializationStatus.UNINITIALIZED;
		}

		return button.$dropDown;
	}

	private showDropDown(button: Button) {
		let $row = this.$buttonRows.filter($row => $.contains($row[0], button.$buttonWrapper[0]))[0];
		UiButtonGroup.logger.debug(button.$dropDown[0].offsetHeight);
		const me = this;
		button.$dropDown.hide().insertAfter($row).slideDown({
			duration: 200,
			progress: (animation, progress: number, remainingMs: number) => {
				let buttonHeight = button.$buttonWrapper[0].offsetHeight;
				let dropDownHeight = button.$dropDown[0].offsetHeight;
				let totalInterestingPartHeight = buttonHeight + dropDownHeight;
				let buttonY = button.$buttonWrapper.offset().top - me.getMainDomElement().closest('.UiToolAccordion').offset().top;
				let $scrollContainer = me.getMainDomElement().scrollParent();
				let scrollY = $scrollContainer.scrollTop();
				let viewPortHeight = $scrollContainer[0].offsetHeight;

				if (scrollY < buttonY + totalInterestingPartHeight - viewPortHeight) {
					scrollY = buttonY + totalInterestingPartHeight - viewPortHeight;
					$scrollContainer.scrollTop(scrollY);
					UiButtonGroup.logger.debug(scrollY);
				}
				if (buttonY < scrollY) { // TODO scrollY
					$scrollContainer.scrollTop(buttonY);
				}
			}
		});
		(<any>button.$dropDownSourceButtonIndicator).position({
			my: "center bottom",
			at: "center bottom",
			of: button.$buttonWrapper
		});
	}

	public setDropDownData(buttonId: string, itemViewConfig: UiItemViewConfig) {
		let button = this.buttonsById[buttonId];
		button.dropDownItemViewConfig = itemViewConfig;
		this.initDropDownItemView(button);
	}

	private initDropDownItemView(button: Button) {
		if (!button) {
			return;
		}

		button.dropDownItemView && button.dropDownItemView.destroy();
		button.$dropDown.find(":not(.source-button-indicator)").remove();
		button.dropDownItemView = new UiItemView(button.dropDownItemViewConfig, this.context);
		button.dropDownItemView.onItemClicked.addListener(eventObject => {
			this.toolAccordion.onToolbarDropDownItemClick.fire(EventFactory.createAbstractUiToolContainer_ToolbarDropDownItemClickEvent(this.toolAccordionId, this.config.groupId, button.config.buttonId, eventObject.groupId, eventObject.itemId));
		});
		button.dropDownItemView.getMainDomElement().appendTo(button.$dropDown);
		button.dropDownItemView.attachedToDom = this.attachedToDom;
		button.dropDownInitializationStatus = DropDownInitializationStatus.INITIALIZED;
	}

	public getMaxOptimizedButtonWidth(): number {
		return this.buttons
			.filter(button => button.visible)
			.reduce((maxWidth, button) => Math.max(maxWidth, button.optimizedWidth), 0);
	}

	public setEnforcedButtonWidth(enforcedButtonWidth: number) {
		this.enforcedButtonWidth = enforcedButtonWidth;
		this.updateRows();
	}

	public setButtonVisible(buttonId: string, visible: boolean) {
		const button = this.buttonsById[buttonId];
		if (button) {
			button.$buttonWrapper.toggleClass("hidden", !visible);
			button.visible = visible;
			this.updateVisibility();
			this.updateRows();
		}
	}

	public addButton(buttonConfig: UiToolbarButtonConfig, neighborButtonId?: string, beforeNeighbor?: boolean) {
		const button = this.createButton(buttonConfig);

		const existingButton = this.buttonsById[buttonConfig.buttonId];
		if (existingButton) {
			this.removeButton(buttonConfig.buttonId);
		}

		this.buttonsById[buttonConfig.buttonId] = button;
		const neighborButton = this.buttonsById[neighborButtonId];
		if (neighborButton) {
			let neighborButtonIndex = this.buttons.indexOf(neighborButton);
			if (beforeNeighbor) {
				this.buttons.splice(neighborButtonIndex, 0, button);
			} else {
				this.buttons.splice(neighborButtonIndex + 1, 0, button);
			}
		} else {
			this.buttons.push(button);
		}

		this.updateVisibility();
		this.updateRows();
	}

	public removeButton(buttonId: string): void {
		const button = this.buttonsById[buttonId];

		if (button) {
			delete this.buttonsById[buttonId];
			this.buttons = this.buttons.filter(b => b.config.buttonId !== buttonId);
			button.$buttonWrapper.remove();
		}

		// TODO destroy dropdown

		this.updateVisibility();
		this.updateRows();
	}


	private updateRows() {
		let availableWidth = this.$buttonGroupWrapper[0].offsetWidth;
		if (!this.attachedToDom || !availableWidth) {
			return;
		}

		this.$buttonRows.forEach($row => {
			$row.remove();
			$row.empty();
		});
		let buttonsPerRow = Math.floor(availableWidth / Math.max(16, this.enforcedButtonWidth));

		if (buttonsPerRow === 0) {
			buttonsPerRow = 1;
		}

		let visibleButtonsCount = 0;
		for (let i = 0; i < this.buttons.length; i++) {
			let button = this.buttons[i];
			if (button.visible) {
				let rowIndex = Math.floor(visibleButtonsCount / buttonsPerRow);
				let $row = this.$buttonRows[rowIndex] || (this.$buttonRows[rowIndex] = $(`<div class="button-row">`));
				button.$buttonWrapper
					.css("flex-basis", this.enforcedButtonWidth + "px")
					.appendTo($row);
				visibleButtonsCount++;
			}
		}
		// fill with dummy elements
		for (let i = visibleButtonsCount; i % buttonsPerRow != 0; i++) {
			let rowIndex = Math.floor(i / buttonsPerRow);
			let $row = this.$buttonRows[rowIndex];
			$row.append(`<div class="row-filler" style="flex-basis: ${this.enforcedButtonWidth}px">`);
		}

		this.$buttonRows.forEach($row => {
			if ($row.has("*")) {
				$row.appendTo(this.$buttonGroup);
			}
		});
	}

	public setVisible(visible: boolean) {
		this.visible = visible;
		this.updateVisibility();
	}

	public isVisible() {
		return this.visible;
	}

	private updateVisibility() {
		let hasVisibleButton = this.buttons.some(b => b.visible);
		this.$buttonGroupWrapper.toggleClass("hidden", !(this.visible && hasVisibleButton));
	}

	public getMainDomElement(): JQuery {
		return this.$buttonGroupWrapper;
	}

	public onAttachedToDom() {
		this.attachedToDom = true;
		this.buttons.forEach(b => {
			b.dropDownItemView && (b.dropDownItemView.attachedToDom = true);
		});


		let me = this;
		this.getMainDomElement().on("click", ".toolbar-button-wrapper", function (e) {
			let button = me.buttonsById[(<Element>this).getAttribute("data-buttonId")];
			if (button.config.togglesFullScreenOnComponent) {
				if (isFullScreen()) {
					exitFullScreen();
				} else {
					enterFullScreen(me.context.getComponentById(button.config.togglesFullScreenOnComponent));
				}
			}
			if (button.config.openNewTabWithUrl) {
				window.open(button.config.openNewTabWithUrl, '_blank');
			}

			let dropdownClickInfo: UiDropDownButtonClickInfoConfig = null;
			if (button.config._type === 'UiToolbarDropDownButton') {
				let dropdownVisible = button.$dropDown.is(":visible");
				dropdownClickInfo = createUiDropDownButtonClickInfoConfig(!dropdownVisible, button.dropDownInitializationStatus === DropDownInitializationStatus.INITIALIZED || !!button.dropDownItemViewConfig);
				if (!dropdownVisible) {
					if (button.dropDownItemViewConfig && !button.dropDownItemView) {
						me.initDropDownItemView(button);
					}
					me.showDropDown(button);
				} else {
					button.$dropDown.slideUp(200);
				}

				if (button.dropDownInitializationStatus === DropDownInitializationStatus.UNINITIALIZED) {
					button.dropDownInitializationStatus = DropDownInitializationStatus.LOADING;
				}
			}

			me.toolAccordion.onToolbarButtonClick.fire(EventFactory.createAbstractUiToolContainer_ToolbarButtonClickEvent(me.toolAccordionId, me.config.groupId, button.config.buttonId, dropdownClickInfo));
		}).on("blur", ".toolbar-button-wrapper", function (e) {
			let button = me.buttonsById[(<Element>this).getAttribute("data-buttonId")];
			button.$dropDown && button.$dropDown.slideUp(200);
		});

		this.updateRows();
	}

	public onResize(): void {
		this.updateRows();
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiToolAccordion", UiToolAccordion);